import { IProject } from 'spw-redux/modules/projects/types';
import {
  AlternativeWorkpointsBreakdown,
  ISpacesBreakDown,
} from 'spw-redux/modules/program-summary/types';
import {
  ALTERNATIVE_WORKPOINTS_CARDS,
  PROGRAM_SUMMARY_CARDS,
} from 'spw-esa-projects/constants';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';
import { SpaceTypeColor } from 'spw-redux/modules/space_types/types';
import { PlainWorksheet } from '../../../models/Worksheet';
import { PlainProgramScenario } from '../../../models/Scenario';

export type FileType = 'png' | 'pdf';
export const PNG = 'png';
export const PDF = 'pdf';

export type ScenarioReportType =
  | 'worksheet'
  | 'worksheetWithNotes'
  | 'worksheetAndSummaryCalculations'
  | 'programSummary'
  | 'programSummaryOnePager'
  | 'seatCountAnalysis'
  | 'summaryCalculations'
  | 'notesByCategory'
  | 'allNotes';

export type EntityType = 'BUILDING' | 'FLOOR' | 'SCENARIO' | 'TOTAL_PROGRAM';

export type ScenarioExportable =
  | 'worksheet'
  | 'programSummary'
  | 'programSummaryOnePager'
  | 'summaryCalculations'
  | 'notes'
  | 'seatCountAnalysis';

export type ProgramSummaryExportableType =
  | 'allCardsIndividually'
  | 'onePager'
  | 'summaryData'
  | 'individualWorkspacesCards'
  | 'meetingSpacesCards'
  | 'amenitySpacesCards'
  | 'supportSpacesCards';

export type ESAProjectReportType = 'programSummary' | 'occupancyAndUtilization';
export type ProgrammingProjectReportType = 'programSummary';

export type ESAProjectExportable =
  | 'programSummary'
  | 'occupancyAndUtilizationCards';

export type ESAProgramSummaryExportable =
  | typeof PROGRAM_SUMMARY_CARDS.PROJECT_OVERVIEW
  | typeof PROGRAM_SUMMARY_CARDS.KEY_DATA_POINTS
  | typeof PROGRAM_SUMMARY_CARDS.SPACE_TYPE_ALLOCATION_IWS_USF
  | typeof PROGRAM_SUMMARY_CARDS.SPACE_TYPE_ALLOCATION_SUBTOTAL_USF
  | typeof PROGRAM_SUMMARY_CARDS.HEADCOUNT_USF
  | typeof PROGRAM_SUMMARY_CARDS.SEAT_COUNT_USF
  | typeof PROGRAM_SUMMARY_CARDS.DESK_SHARING_RATIO
  | typeof PROGRAM_SUMMARY_CARDS.USF_BREAKDOWN
  | typeof PROGRAM_SUMMARY_CARDS.MEETING_SEATS_RATIO
  | typeof ALTERNATIVE_WORKPOINTS_CARDS.WORKPOINTS
  | typeof ALTERNATIVE_WORKPOINTS_CARDS.SEAT_COUNT_USF_WITH_WORKPOINTS
  | typeof ALTERNATIVE_WORKPOINTS_CARDS.DESK_SHARING_WITH_WORKPOINTS
  | typeof ALTERNATIVE_WORKPOINTS_CARDS.ALTERNATIVE_WORKPOINTS_BREAKDOWN
  | 'individualWorkspacesUsfMix'
  | 'individualWorkspacesSeatMix'
  | 'individualWorkspacesUsfAndSeatMix'
  | 'meetingSpacesUsfMix'
  | 'meetingSpacesRatios'
  | 'meetingSpacesUsfMixAndRatios'
  | 'amenitySpacesUsfMix'
  | 'amenitySpacesRatios'
  | 'amenitySpacesUsfMixAndRatios'
  | 'supportSpacesUsfMix'
  | 'supportSpacesRatios'
  | 'supportSpacesUsfMixAndRatios';

export type OccupancyAndUtilizationExportable = 'occupancyAndUtilizationCards';

export enum ProgrammingProgramSummaryExportables {
  onePager = 'onePager',
  individualCards = 'individualCards',
}

export enum BenchmarkReportExportables {
  summary = 'benchmarkReportSummary',
}

export enum BenchmarkReportCardExportables {
  reportOverview = 'Report Overview',
  individualWorkspacesAnalysis = 'Individual Workspaces Analysis',
  USFAllocation = 'USF Allocation By Space Category',
  workSeatRatios = 'Work Seat Ratios',
  USFMetrics = 'USF Metrics',
}

export type ReportAttributes = {
  reportType: ScenarioReportType;
  entityId?: number;
  entityType?: EntityType;
  format: FileType;
  category?: string;
  isOpen?: boolean;
  showRsf?: boolean;
  shouldShowClientFacingNames?: boolean;
  showDecimals?: boolean;
  measurementSystem?: MeasurementSystem;
};

export type ESAReportAttributes = ReportAttributes & {
  project: IProject;
  reportType: ESAProjectReportType;
  exportableType:
    | ProgramSummaryExportableType
    | OccupancyAndUtilizationExportable;
  floorIds?: number[];
  buildingIds?: number[];
  roundIds?: number[];
  roundsWithHighestOccupancy?: number[];
  roundsWithLowestOccupancy?: number[];
  breakdown?: ISpacesBreakDown;
  alternativeWorkpointsBreakdown?: AlternativeWorkpointsBreakdown;
  headcountType?: string;
  currentSpaceTypeColors?: { [index: string]: SpaceTypeColor };
  currentProjectColors?: { subcategories: Record<string, string> };
};

export type ProgrammingReportAttributes = ReportAttributes & {
  project: IProject;
  reportType: ProgrammingProjectReportType;
  exportableType: ProgramSummaryExportableType;
  scenario: PlainProgramScenario;
  worksheet: PlainWorksheet;
};

interface StorageItem {
  name: string;
  value: string;
}

export interface ReportEntry {
  entityId?: number;
  entityType?: EntityType;
  exportable:
    | ScenarioExportable
    | ESAProjectExportable
    | BenchmarkReportExportables
    | null;
  queryParams?: { field: string; value: string };
  variants?: {
    isOpen?: boolean;
    showNotes?: boolean;
    category?: string;
    shouldShowClientFacingNames?: boolean;
    showRsf?: boolean;
    headcountType?: string;
  };
  screenshotsOpts: {
    format: string;
    exportable?: ESAProgramSummaryExportable;
    width: number;
    clip?: { x: number; y: number; width: number; height: number };
    localStorageItems?: StorageItem[];
  }[];
}

type BrowserPageAction = 'CLICK' | 'WAIT_FOR_HIDDEN_STATE';

export type PageAction = {
  action: BrowserPageAction;
  selector: string;
};

export type ScreenshotOpts = {
  format: string;
  exportable?: ESAProgramSummaryExportable;
  width: number;
  clip?: { x: number; y: number; width: number; height?: number };
  pageActions?: PageAction[];
  localStorageItems?: StorageItem[];
};

export interface ESAReportEntry {
  entityId?: number;
  entityType?: EntityType;
  exportable: ESAProjectExportable;
  variants?: {
    isOpen?: boolean;
    shouldShowClientFacingNames?: boolean;
    showRsf?: boolean;
    headcountType?: string;
    roundIds?: string;
    roundId?: number;
    buildingIds?: string;
    floorIds?: string;
  };
  screenshotsOpts: ScreenshotOpts[];
}

export type ProgrammingReportEntry = ReportEntry;
